import { captureAction, captureError } from '@smg-real-estate/browser-rum';

import { patchSeonSession } from '@/api/insertions';
import seonAgentUrl from '@seontechnologies/seon-javascript-sdk/agent.esm.js?url';

type SeonSdk = (typeof import('@seontechnologies/seon-javascript-sdk'))['default'];

const SEON_SESSION_CONFIG = {
  geolocation: {
    canPrompt: false,
  },
  networkTimeoutMs: 2000,
  fieldTimeoutMs: 2000,
  region: 'eu',
  silentMode: true,
} as const;

/** Importing the package entry would re-minify the SDK and corrupt its payload (PKP-6139). */
async function loadSeonAgent(): Promise<SeonSdk> {
  const module = await import(/* @vite-ignore */ seonAgentUrl);
  return module.default;
}

function captureSeonError(error: unknown, action: string) {
  const err = error instanceof Error ? error : new Error(String(error));
  const type = err.name === 'SEONConfigError' ? 'SEONConfigError' : 'SEONUnknownError';
  captureError(err, {
    service: 'seon',
    action,
    type,
  });
}

/** Starts SEON behavior collection. Safe to call again after getSession to restart. */
export async function initSeon(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const seon = await loadSeonAgent();
    seon.init();
  } catch (error) {
    captureSeonError(error, 'init');
  }
}

/** Validates insertion context and starts SEON collection (no getSession). */
export async function configureSeon(payload: {
  insertionId: string;
  userId: string;
}): Promise<void> {
  const { insertionId, userId } = payload;
  if (!insertionId || !userId) {
    captureError(new Error('Missing required parameter: insertionId or userId'), {
      service: 'seon',
      action: 'configure',
    });
    return;
  }

  await initSeon();
}

/**
 * Ends SEON collection, patches the fingerprint to the API, then restarts collection
 * so failed submits / guest login interrupts can collect again.
 */
export async function saveSeonSession({
  ownerId,
  insertionId,
}: {
  ownerId: string;
  insertionId: string;
}): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  let fingerprint: string | undefined;

  try {
    const seon = await loadSeonAgent();
    fingerprint = await seon.getSession(SEON_SESSION_CONFIG);
    captureAction('SEON Session captured successfully');
  } catch (error) {
    captureSeonError(error, 'getSession');
    return;
  }

  try {
    await patchSeonSession(ownerId, insertionId, fingerprint);
  } catch {
    // The error is captured in the API layer, so we can fail silently here.
  }

  await initSeon();
}
